import { useState } from "react";
import {
  Box,
  CircularProgress,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Button,
  Grid,
  MenuItem,
  TextField,
  Tooltip,
} from "@mui/material";
import { SummarizeOutlined, TranscribeOutlined, WarningRounded } from "@mui/icons-material";
import { useQuery } from "@tanstack/react-query";
import dayjs from "dayjs";
import ContentBox from "../ContentBox/ContentBox";
import ExpandableList from "../ExpandableList/ExpandableList";
import ToolTipIconButton from "../Button/ToolTipIconButton";
import LegalbirdIoModal from "../Modal/LegalbirdIoModal";
import { useCurrentUser } from "../../provider/CurrentUserProvider";
import { fetchCollection, fetchResource, queryKeys } from "../../services/ReactQuery/reactQueryService";
import { userHasOneOfTheseRoles } from "../../services/backofficeUserService";
import { Roles } from "../../types/BackofficeUser";
import { HydraCollectionResult } from "../../types/HydraCollectionResult";
import {
  PhoneCall,
  PhoneCallAnonymizedTranscriptionResponse,
  PhoneCallContentResponse,
  PhoneCallSummaryResponse,
} from "../../types/PhoneCall";
import { scrollToAnchor } from "../../theme/commonStyles.ts";
import { useBackofficeUser } from "../../provider/BackofficeUserProvider";
import { filterPhoneCalls, isPhoneCallProcessing, phoneCallOutcome, phoneCallOutcomeLabels, phoneCallRecipientLabel, phoneCallStatusLabels } from "../../services/phoneCallService";
import CaseCallDocumentation from "./CaseCallDocumentation";
import ReactMarkdown from "react-markdown";
import { PresenceAvatar } from "../NavigationBar/NavigationBarPresence/NavigationBarPresence";

const PHONE_CALL_MODAL_CONFIG = {
  summary: {
    resource: "lb/services/phone_call_summary",
    title: "Gesprächszusammenfassung",
    loadingText: "Zusammenfassung wird geladen.",
    errorText: "Zusammenfassung konnte nicht geladen werden.",
    emptyText: "Keine Zusammenfassung vorhanden.",
  },
  transcription: {
    resource: "lb/services/phone_call_anonymized_transcription",
    title: "Anonymisierte Transkription",
    loadingText: "Transkription wird geladen.",
    errorText: "Transkription konnte nicht geladen werden.",
    emptyText: "Keine anonymisierte Transkription vorhanden.",
  },
} as const;

type PhoneCallContentType = keyof typeof PHONE_CALL_MODAL_CONFIG;

// Legalbird caps collections at 30; keeping this explicit also preserves correct proxy metadata on the final page.
const PHONE_CALLS_PER_PAGE = 30;

const fetchPhoneCalls = async (caseIri: string, signal: AbortSignal) => {
  const phoneCalls: PhoneCall[] = [];
  let page = 1;
  let totalItems = 0;

  do {
    const collection = (await fetchCollection(
      "phone_calls",
      {
        case: caseIri,
        page,
        itemsPerPage: PHONE_CALLS_PER_PAGE,
      },
      { signal },
    )) as HydraCollectionResult<PhoneCall>;
    const currentPage = collection["hydra:member"];

    phoneCalls.push(...currentPage);
    totalItems = collection["hydra:totalItems"];
    page += 1;

    if (currentPage.length === 0) {
      break;
    }
  } while (phoneCalls.length < totalItems);

  return phoneCalls;
};

const formatCallTimestamp = (phoneCall: PhoneCall) => {
  const start = dayjs(phoneCall.created);

  if (!phoneCall.created || !start.isValid()) {
    return "-";
  }

  return `${start.format("DD.MM.YYYY, HH:mm")} Uhr`;
};

const formatDuration = (phoneCall: PhoneCall) => {
  const durationInSeconds = phoneCall.callDuration;
  if (durationInSeconds == null || durationInSeconds < 0) {
    return "–";
  }

  const hours = Math.floor(durationInSeconds / 3600);
  const minutes = Math.floor((durationInSeconds % 3600) / 60);
  const seconds = durationInSeconds % 60;

  return [hours, minutes, seconds].map((value) => String(value).padStart(2, "0")).join(":");
};

type PhoneCallsProps = {
  product: { id: number };
};

type PhoneCallContentSelection = {
  phoneCallId: number;
  contentType: PhoneCallContentType;
};

type PhoneCallContentModalProps = PhoneCallContentSelection & {
  handleClose: () => void;
};

const PhoneCallContentModal = ({ phoneCallId, contentType, handleClose }: PhoneCallContentModalProps) => {
  const config = PHONE_CALL_MODAL_CONFIG[contentType];
  const {
    data: content,
    isPending,
    isError,
  } = useQuery<PhoneCallContentResponse, Error, string | null>({
    queryKey: queryKeys.item(config.resource, phoneCallId),
    queryFn: () => fetchResource(config.resource, phoneCallId),
    select: (response) =>
      contentType === "summary"
        ? (response as PhoneCallSummaryResponse).summary
        : (response as PhoneCallAnonymizedTranscriptionResponse).anonymizedTranscription,
  });

  return (
    <LegalbirdIoModal handleClose={handleClose} open title={config.title} hasActions={false} fullWidth maxWidth="md">
      {isPending && (
        <Stack direction="row" spacing={2} sx={{ alignItems: "center" }}>
          <CircularProgress size={24} />
          <Typography>{config.loadingText}</Typography>
        </Stack>
      )}
      {isError && <Typography color="error">{config.errorText}</Typography>}
      {!isPending && !isError && !content && <Typography>{config.emptyText}</Typography>}
      {!isPending &&
        !isError &&
        content &&
        (contentType === "summary" ? (
          <Box dangerouslySetInnerHTML={{ __html: content }} />
        ) : (
          <Box sx={{ whiteSpace: "pre-wrap", overflowWrap: "anywhere" }}>{content}</Box>
        ))}
    </LegalbirdIoModal>
  );
};

const PhoneCalls = ({ product }: PhoneCallsProps) => {
  const currentUser = useCurrentUser();
  const { getUserData } = useBackofficeUser();
  const [selectedPhoneCallContent, setSelectedPhoneCallContent] = useState<PhoneCallContentSelection | null>(null);
  const [selectedCaseCall, setSelectedCaseCall] = useState<PhoneCall | null>(null);
  const [recipientFilter, setRecipientFilter] = useState("");
  const [callerFilter, setCallerFilter] = useState("");
  const [outcomeFilter, setOutcomeFilter] = useState("");
  const caseIri = `/abstract_cases/${product.id}`;
  const canViewAnonymizedTranscription = userHasOneOfTheseRoles(currentUser, [
    Roles.admin,
    Roles.productManager,
    Roles.developer,
  ]);
  const columnCount = canViewAnonymizedTranscription ? 7 : 6;
  const {
    data: phoneCallCollection,
    isPending: phoneCallsArePending,
    isError: phoneCallsHaveError,
  } = useQuery<PhoneCall[]>({
    queryKey: [...queryKeys.collection("phone_calls", { case: caseIri }), "allPages"],
    queryFn: ({ signal }) => fetchPhoneCalls(caseIri, signal),
    refetchInterval: (query) => query.state.data?.some((call) => call.isCaseCall && (!call.finishedDate || isPhoneCallProcessing(call))) ? 3000 : false,
  });

  const phoneCalls = [...(phoneCallCollection ?? [])].sort((first, second) => {
    const firstStart = first.created && dayjs(first.created).isValid() ? dayjs(first.created).valueOf() : 0;
    const secondStart = second.created && dayjs(second.created).isValid() ? dayjs(second.created).valueOf() : 0;

    return secondStart - firstStart;
  });

  const callerName = (call: PhoneCall) => call.backofficeUserId
    ? getUserData(`/backoffice_users/${call.backofficeUserId}`)?.person.fullname ?? "Unbekannt"
    : "Unbekannt";
  const recipientOptions = new Map<string, string>();
  const callerOptions = new Map<string, string>();
  phoneCalls.forEach((call) => {
    const recipient = call.recipientIdentifier ?? call.toNumber;
    if (recipient && !recipientOptions.has(recipient)) {
      recipientOptions.set(recipient, phoneCallRecipientLabel(call));
    }
    if (call.backofficeUserId) {
      callerOptions.set(String(call.backofficeUserId), callerName(call));
    }
  });
  const filteredPhoneCalls = filterPhoneCalls(phoneCalls, recipientFilter, callerFilter, outcomeFilter);

  return (
    <>
      <Box sx={scrollToAnchor} id={"phonecalls"} />
      <ContentBox headline="Telefonate">
        <Grid container spacing={2} sx={{ mb: 2 }}>
          <Grid size={{ xs: 12, md: 4 }}>
            <TextField select fullWidth label="Partei / Kontakt" value={recipientFilter} onChange={(event) => setRecipientFilter(event.target.value)}>
              <MenuItem value="">Alle</MenuItem>
              {[...recipientOptions].map(([value, label]) => <MenuItem key={value} value={value}>{label}</MenuItem>)}
            </TextField>
          </Grid>
          <Grid size={{ xs: 12, md: 4 }}>
            <TextField select fullWidth label="Angerufen von" value={callerFilter} onChange={(event) => setCallerFilter(event.target.value)}>
              <MenuItem value="">Alle</MenuItem>
              {[...callerOptions].map(([value, label]) => <MenuItem key={value} value={value}>{label}</MenuItem>)}
            </TextField>
          </Grid>
          <Grid size={{ xs: 12, md: 4 }}>
            <TextField select fullWidth label="Ergebnis" value={outcomeFilter} onChange={(event) => setOutcomeFilter(event.target.value)}>
              <MenuItem value="">Alle</MenuItem>
              {Object.entries(phoneCallOutcomeLabels).map(([value, label]) => <MenuItem key={value} value={value}>{label}</MenuItem>)}
            </TextField>
          </Grid>
          {(recipientFilter || callerFilter || outcomeFilter) && <Grid size={12}>
            <Button onClick={() => {
              setRecipientFilter("");
              setCallerFilter("");
              setOutcomeFilter("");
            }}>Filter zurücksetzen</Button>
          </Grid>}
        </Grid>
        <ExpandableList
          key={`${product.id}:${recipientFilter}:${callerFilter}:${outcomeFilter}`}
          items={filteredPhoneCalls}
          renderItems={(phoneCallsToDisplay) => (
            <TableContainer>
              <Table sx={{ overflowWrap: "anywhere" }}>
                <TableHead>
                  <TableRow>
                    <TableCell padding="none" />
                    <TableCell>Zeitpunkt</TableCell>
                    <TableCell>Dauer</TableCell>
                    <TableCell>Angerufen</TableCell>
                    <TableCell>Ergebnis</TableCell>
                    <TableCell padding="none" />
                    {canViewAnonymizedTranscription && <TableCell padding="none" />}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {phoneCallsArePending && (
                    <TableRow>
                      <TableCell colSpan={columnCount} align="center">
                        <CircularProgress />
                      </TableCell>
                    </TableRow>
                  )}
                  {phoneCallsHaveError && (
                    <TableRow>
                      <TableCell colSpan={columnCount} align="center">
                        <Typography color="error">Telefonate konnten nicht geladen werden.</Typography>
                      </TableCell>
                    </TableRow>
                  )}
                  {!phoneCallsArePending && !phoneCallsHaveError && filteredPhoneCalls.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={columnCount} align="center">
                        Keine passenden Telefonate vorhanden.
                      </TableCell>
                    </TableRow>
                  )}
                  {phoneCallsToDisplay.map((phoneCall) => (
                    <TableRow key={phoneCall.id}>
                      <TableCell padding="none" sx={{ pl: 0.5 }}>
                        <Tooltip title={callerName(phoneCall)}>
                          <Box sx={{ width: "fit-content" }}>
                            <PresenceAvatar displayName={callerName(phoneCall)} isCurrentUser={phoneCall.backofficeUserId === currentUser.id} />
                          </Box>
                        </Tooltip>
                      </TableCell>
                      <TableCell>{formatCallTimestamp(phoneCall)}</TableCell>
                      <TableCell>{formatDuration(phoneCall)}</TableCell>
                      <TableCell>
                        {phoneCall.recipientIdentifier && phoneCall.recipientLabel ? (
                          <Tooltip title={[phoneCall.recipientLabel, phoneCall.toNumber].filter(Boolean).join(" – ")}>
                            <span>{phoneCall.recipientLabel.split(" – ", 1)[0]}</span>
                          </Tooltip>
                        ) : phoneCallRecipientLabel(phoneCall)}
                      </TableCell>
                      <TableCell>
                        {phoneCall.documentationPending && (
                          <Tooltip title="Nachbearbeitung offen">
                            <WarningRounded color="info" sx={{ verticalAlign: "middle", mr: 0.5 }} />
                          </Tooltip>
                        )}
                        {phoneCallOutcomeLabels[phoneCallOutcome(phoneCall) ?? ""] ?? phoneCallStatusLabels[phoneCall.callStatus ?? ""] ?? "Unbekannt"}
                      </TableCell>
                      <TableCell padding="none" align="center">
                        {phoneCall.isCaseCall && (phoneCall.note || (!currentUser.isExternal && phoneCall.backofficeUserId === currentUser.id)) && (
                          <ToolTipIconButton icon={SummarizeOutlined} tooltip="Telefonvermerk öffnen" onClick={() => setSelectedCaseCall(phoneCall)} />
                        )}
                        {!phoneCall.isCaseCall && phoneCall.callSummary && (
                          <ToolTipIconButton
                            icon={SummarizeOutlined}
                            tooltip="Gesprächszusammenfassung öffnen"
                            onClick={() =>
                              setSelectedPhoneCallContent({ phoneCallId: phoneCall.id, contentType: "summary" })
                            }
                          />
                        )}
                      </TableCell>
                      {canViewAnonymizedTranscription && (
                        <TableCell padding="none" align="center">
                          {phoneCall.anonymizedTranscription && (
                            <ToolTipIconButton
                              icon={TranscribeOutlined}
                              tooltip="Anonymisierte Transkription öffnen"
                              onClick={() =>
                                setSelectedPhoneCallContent({ phoneCallId: phoneCall.id, contentType: "transcription" })
                              }
                            />
                          )}
                        </TableCell>
                      )}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        />
        {selectedCaseCall && <LegalbirdIoModal open handleClose={() => setSelectedCaseCall(null)} title="Telefonvermerk" cancelButtonLabel="Schließen" disableBackdropClick fullWidth maxWidth="md">
          {!currentUser.isExternal && selectedCaseCall.backofficeUserId === currentUser.id
            ? <CaseCallDocumentation key={selectedCaseCall.id} phoneCallId={selectedCaseCall.id} onClose={() => setSelectedCaseCall(null)} />
            : <ReactMarkdown>{selectedCaseCall.note ?? "Kein Vermerk vorhanden."}</ReactMarkdown>}
        </LegalbirdIoModal>}
        {selectedPhoneCallContent &&
          (selectedPhoneCallContent.contentType === "summary" || canViewAnonymizedTranscription) && (
            <PhoneCallContentModal
              {...selectedPhoneCallContent}
              handleClose={() => setSelectedPhoneCallContent(null)}
            />
          )}
      </ContentBox>
    </>
  );
};

export default PhoneCalls;
